import { Droplets, Repeat, CircleDollarSign } from "lucide-react";
import { Pct } from "@/components/layernav/primitives";
import { fmtBig } from "./SectorHero";

// Significance thresholds mirror the backend DeFi driver thresholds (routes_public)
const T = { tvl: 0.25, dex: 2.0, stables: 0.15 };

const ctxTvl = (v: number | null) => v == null ? "24H change unavailable."
  : v >= T.tvl ? "Broad protocol TVL expanded over the last 24H."
    : v <= -T.tvl ? "Broad protocol TVL contracted over the last 24H."
      : "Broad protocol TVL held steady over the last 24H.";
const ctxDex = (v: number | null) => v == null ? "24H change unavailable."
  : v >= T.dex ? "Decentralized exchange activity accelerated over the last 24H."
    : v <= -T.dex ? "Decentralized exchange activity cooled over the last 24H."
      : "Decentralized exchange activity held steady over the last 24H.";
const ctxStables = (v: number | null) => v == null ? "24H change unavailable."
  : v >= T.stables ? "Stablecoin supply continued to expand."
    : v <= -T.stables ? "Stablecoin supply contracted over the last 24H."
      : "Stablecoin supply was flat over the last 24H.";

// Correlational condition summary from real deltas — never causal language
const summary = (tvl: number | null, dex: number | null, st: number | null) => {
  const parts: { mag: number; text: string }[] = [];
  if (dex != null && Math.abs(dex) >= T.dex)
    parts.push({ mag: Math.abs(dex) / T.dex, text: `DEX volume ${dex > 0 ? "rose" : "fell"} ${Math.abs(dex).toFixed(2)}%` });
  if (tvl != null && Math.abs(tvl) >= T.tvl)
    parts.push({ mag: Math.abs(tvl) / T.tvl, text: `total TVL ${tvl > 0 ? "expanded" : "contracted"} ${Math.abs(tvl).toFixed(2)}%` });
  if (st != null && Math.abs(st) >= T.stables)
    parts.push({ mag: Math.abs(st) / T.stables, text: `stablecoin supply ${st > 0 ? "expanded" : "contracted"}` });
  if (!parts.length) return "DeFi conditions are steady over the last 24H.";
  parts.sort((a, b) => b.mag - a.mag);
  const s = parts.slice(0, 2).map((p) => p.text).join(" while ");
  return s.charAt(0).toUpperCase() + s.slice(1) + " over the last 24H.";
};

const TONES: Record<string, { text: string; ring: string; glow: string; hex: string }> = {
  purple: { text: "text-[#A78BFA]", ring: "border-[#A78BFA]/35", glow: "bg-[#A78BFA]/10", hex: "#A78BFA" },
  cyan: { text: "text-[#00E5FF]", ring: "border-[#00E5FF]/35", glow: "bg-[#00E5FF]/10", hex: "#00E5FF" },
  mint: { text: "text-[#34D399]", ring: "border-[#34D399]/35", glow: "bg-[#34D399]/10", hex: "#34D399" },
};

/* Real-history gradient-fill micro sparkline. Hidden below 4 points — never synthesized. */
const AreaSpark = ({ points, hex, id, label }: { points: number[]; hex: string; id: string; label: string }) => {
  if (!points || points.length < 4) return null;
  const min = Math.min(...points), max = Math.max(...points);
  const span = max - min || 1;
  const pts = points.map((p, i) =>
    `${((i / (points.length - 1)) * 100).toFixed(1)},${(24 - ((p - min) / span) * 20).toFixed(1)}`);
  return (
    <svg viewBox="0 0 100 26" preserveAspectRatio="none" aria-hidden
      className="w-full h-7 mt-2.5 opacity-90" data-testid={`ld-spark-${id}`}>
      <title>{label}</title>
      <defs>
        <linearGradient id={`ldg-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hex} stopOpacity="0.28" />
          <stop offset="1" stopColor={hex} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon fill={`url(#ldg-${id})`} points={`0,26 ${pts.join(" ")} 100,26`} />
      <polyline fill="none" stroke={hex} strokeWidth="1" strokeOpacity="0.75"
        points={pts.join(" ")} vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const DriverCard = ({ icon: Icon, tone, label, value, delta, context, testid, spark, sparkLabel }: {
  icon: any; tone: string; label: string; value: string; delta: number | null;
  context: string; testid: string; spark: number[]; sparkLabel: string;
}) => {
  const t = TONES[tone];
  return (
    <div data-testid={testid} className="relative flex flex-col rounded-xl border border-white/[0.09] bg-black/55 backdrop-blur-md p-4 min-w-0">
      <div className="flex items-center gap-2.5">
        <span className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${t.ring} ${t.glow}`}>
          <Icon aria-hidden className={`relative h-4 w-4 ${t.text}`} />
        </span>
        <p className={`font-mono text-[9.5px] font-bold uppercase tracking-[0.2em] ${t.text}`}>{label}</p>
      </div>
      <div className="flex items-baseline gap-2 mt-3">
        <span className="font-mono text-xl font-bold text-white">{value}</span>
        {delta != null && <Pct v={delta} className="!text-[11px]" />}
      </div>
      <p className="text-[11.5px] text-gray-400 leading-relaxed mt-1.5">{context}</p>
      <div className="mt-auto">
        <AreaSpark points={spark} hex={t.hex} id={testid.replace("ld-card-", "")} label={sparkLabel} />
      </div>
    </div>
  );
};

export const LiquidityDrivers = ({ pulse, protocols }: { pulse: any; protocols: any[] }) => {
  if (!pulse || (pulse.tvl_usd == null && pulse.dex_volume_24h_usd == null && pulse.stablecoin_mcap_usd == null)) return null;
  const tvl = pulse.tvl_change_24h_pct ?? null;
  const dex = pulse.dex_volume_change_24h_pct ?? null;
  const st = pulse.stablecoin_change_24h_pct ?? null;
  const hist = pulse.history || {};
  const movers = (protocols || []).filter((p) => p.tvl_change_24h_pct != null);
  const signal = movers.length
    ? movers.reduce((a, b) => (Math.abs(b.tvl_change_24h_pct) > Math.abs(a.tvl_change_24h_pct) ? b : a))
    : null;
  const showSignal = signal && Math.abs(signal.tvl_change_24h_pct) >= 5;
  return (
    <div className="mt-auto pt-7" data-testid="liquidity-drivers">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#00E5FF]">Liquidity Drivers</p>
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-gray-500">Live DeFi conditions · 24H</p>
      </div>
      <p data-testid="ld-summary" className="text-[13px] text-gray-300 mt-2 leading-relaxed max-w-xl">
        {summary(tvl, dex, st)}
      </p>
      <div className="relative mt-3">
        <svg aria-hidden className="hidden sm:block absolute -inset-x-2 -top-5 h-[calc(100%+2.5rem)] w-[calc(100%+1rem)] pointer-events-none"
          viewBox="0 0 900 200" preserveAspectRatio="none">
          <defs>
            <linearGradient id="ldFlow" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#7C3AED" />
              <stop offset="0.5" stopColor="#00E5FF" />
              <stop offset="1" stopColor="#34D399" />
            </linearGradient>
          </defs>
          <path d="M0,58 C180,16 330,140 470,88 S760,36 900,86" fill="none" stroke="url(#ldFlow)"
            strokeWidth="1.4" strokeOpacity="0.4" className="nl-flow-strand" />
          <path d="M0,132 C200,172 360,48 540,110 S780,162 900,118" fill="none" stroke="url(#ldFlow)"
            strokeWidth="1" strokeOpacity="0.26" className="nl-flow-strand-slow" />
        </svg>
        <div className="relative grid sm:grid-cols-3 gap-3 items-stretch">
          <DriverCard icon={Droplets} tone="purple" label="TVL Momentum" testid="ld-card-tvl"
            value={fmtBig(pulse.tvl_usd)} delta={tvl} context={ctxTvl(tvl)}
            spark={hist.tvl || []} sparkLabel="14-day total DeFi TVL · DeFiLlama" />
          <DriverCard icon={Repeat} tone="cyan" label="DEX Volume" testid="ld-card-dex"
            value={fmtBig(pulse.dex_volume_24h_usd)} delta={dex} context={ctxDex(dex)}
            spark={hist.dex_volume || []} sparkLabel="24H DEX volume per NewsLayer snapshot" />
          <DriverCard icon={CircleDollarSign} tone="mint" label="Stablecoin Supply" testid="ld-card-stables"
            value={fmtBig(pulse.stablecoin_mcap_usd)} delta={st} context={ctxStables(st)}
            spark={hist.stablecoins || []} sparkLabel="Stablecoin supply per NewsLayer snapshot" />
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        {showSignal ? (
          <span data-testid="ld-protocol-signal"
            className="inline-flex items-center gap-2 rounded-full border border-white/[0.14] bg-white/[0.05] backdrop-blur-sm px-3 py-1">
            <span className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-[#A78BFA]">Protocol signal</span>
            <span className="font-mono text-[11px] font-bold text-white">{signal.protocol} TVL</span>
            <Pct v={signal.tvl_change_24h_pct} className="!text-[11px]" />
          </span>
        ) : <span aria-hidden />}
        <p className="font-mono text-[8.5px] uppercase tracking-wider text-gray-500">Intelligence visualization · Metrics · DeFiLlama</p>
      </div>
    </div>
  );
};
